import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import { Toaster } from "sonner";

const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ProductsPage = lazy(() => import("@/pages/ProductsPage"));
const CustomersPage = lazy(() => import("@/pages/CustomersPage"));
const InvoiceCreatePage = lazy(() => import("@/pages/InvoiceCreatePage"));
const InvoicesPage = lazy(() => import("@/pages/InvoicesPage"));
const InvoicePrintPage = lazy(() => import("@/pages/InvoicePrintPage"));
const PurchasesPage = lazy(() => import("@/pages/PurchasesPage"));
const StockRegisterPage = lazy(() => import("@/pages/StockRegisterPage"));
const GSTReportPage = lazy(() => import("@/pages/GSTReportPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const ExpensesPage = lazy(() => import("@/pages/ExpensesPage"));
const SalesProfitReportPage = lazy(() => import("@/pages/SalesProfitReportPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const RefundPolicyPage = lazy(() => import("@/pages/RefundPolicyPage"));

const PageLoader = () => (
  <div className="flex items-center justify-center h-40">
    <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
  </div>
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F8F9F6]">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-[#2D5438] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#6B8573]">Loading SuperAgri...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/refund" element={<RefundPolicyPage />} />
            <Route
              path="/invoices/:id/print"
              element={
                <ProtectedRoute>
                  <InvoicePrintPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="invoices/create" element={<InvoiceCreatePage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="purchases" element={<PurchasesPage />} />
              <Route path="stock-registers" element={<StockRegisterPage />} />
              <Route path="gst-reports" element={<GSTReportPage />} />
              <Route path="expenses" element={<ExpensesPage />} />
              <Route path="sales-reports" element={<SalesProfitReportPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="users" element={<UsersPage />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
